import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Video, ShieldCheck, Wifi, FileSignature, Users } from "lucide-react";
import { PageHero } from "@/pages/Agence";

const FAQ = [
  {
    q: "L'AG en ligne est-elle juridiquement valable ?",
    a: "Oui. Depuis le décret 2019-650 du 27 juin 2019, pris en application de l'article 17-1 A de la loi ELAN, l'assemblée générale peut se dérouler en visioconférence. Deux conditions essentielles : l'identification du copropriétaire doit être sécurisée et le vote à distance doit être valide et sûr. La solution du Cabinet GPI satisfait à ces obligations.",
  },
  {
    q: "Que se passe-t-il si j'ai un problème de connexion pendant l'AG ?",
    a: "En cas de coupure, vous pouvez vous reconnecter à tout moment et consulter les résultats des résolutions votées pendant votre absence. C'est exactement comme si vous étiez sorti quelques instants d'une AG classique en présentiel.",
  },
  {
    q: "Comment la feuille de présence est-elle signée ?",
    a: "L'identification sécurisée des participants vaut signature de la feuille de présence. C'est l'une des conditions essentielles à la validité juridique de l'assemblée générale.",
  },
  {
    q: "Peut-on faire une AG hybride (en ligne + en présentiel) ?",
    a: "Tout à fait. Certains copropriétaires peuvent se réunir physiquement à l'heure de l'AG, à condition que chacun dispose d'un appareil (smartphone, tablette ou ordinateur) lui permettant de participer au vote électronique.",
  },
];

const FEATURES = [
  { icon: ShieldCheck, h: "Identification sécurisée", t: "Chaque copropriétaire est authentifié individuellement avant l'AG, dans le respect du cadre légal." },
  { icon: FileSignature, h: "Signature électronique", t: "La feuille de présence est signée électroniquement et conservée comme pièce justificative." },
  { icon: Users, h: "Mode hybride possible", t: "Présentiel et distanciel se combinent sans complexité, chaque participant vote depuis son appareil." },
  { icon: Wifi, h: "Reconnexion à tout moment", t: "En cas de coupure réseau, vous reprenez l'AG là où vous l'aviez laissée." },
];

export default function AGVisio() {
  return (
    <div data-testid="page-ag-visio">
      <PageHero
        overline="Service Cabinet GPI"
        title="Assemblées générales en visioconférence."
        subtitle="La visioconférence au service d'une AG plus efficace, plus participative — dans le strict respect du cadre légal."
      />

      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/metiers/syndic-en-ligne"
            data-testid="ag-back-link"
            className="inline-flex items-center gap-2 text-sm text-[#64748B] hover:text-[#0F172A] transition-colors mb-12"
          >
            <ArrowLeft size={14} /> Retour à Syndic en ligne
          </Link>

          <div className="grid md:grid-cols-2 gap-6">
            {FEATURES.map((f) => (
              <div key={f.h} className="border border-[#E2E8F0] bg-white p-7 flex gap-5">
                <div className="w-12 h-12 border border-[#B8935A] flex items-center justify-center shrink-0">
                  <f.icon size={18} className="text-[#B8935A]" strokeWidth={1.5} />
                </div>
                <div>
                  <h3 className="font-serif text-lg text-[#0F172A]">{f.h}</h3>
                  <p className="text-[#64748B] text-sm mt-2 leading-relaxed">{f.t}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 sm:py-20 bg-[#EFE8DC]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="font-overline-lg text-[#B8935A]">Questions fréquentes</span>
            <h2 className="font-serif text-4xl text-[#0F172A] mt-4">Tout savoir avant votre première AG en visio.</h2>
          </div>
          <div className="space-y-4">
            {FAQ.map((item, i) => (
              <details
                key={i}
                data-testid={`faq-item-${i}`}
                className="group bg-white border border-white p-6 cursor-pointer"
              >
                <summary className="flex items-start justify-between gap-4 list-none">
                  <h3 className="font-serif text-lg text-[#0F172A] flex-1">{item.q}</h3>
                  <span className="font-serif text-2xl text-[#B8935A] group-open:rotate-45 transition-transform shrink-0 leading-none">
                    +
                  </span>
                </summary>
                <p className="text-[#475569] mt-4 leading-relaxed text-[0.95rem]">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 sm:py-20 lg:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Video size={32} className="text-[#B8935A] mx-auto" strokeWidth={1.3} />
          <h2 className="font-serif text-3xl lg:text-4xl text-[#0F172A] mt-6">
            Prêt à essayer ?
          </h2>
          
          <Link
            to="/contact"
            data-testid="ag-cta"
            data-discover="true"
            className="gpi-btn-primary mt-10"
          >
            Contactez nous
            <ArrowUpRight size={14} />
          </Link>
        </div>
      </section>
    </div>
  );
}
